/**
 * RoadsideSpawner - Data-driven roadside object spawning system
 *
 * Spawns objects defined in biome JSON files. Supports any number of object types
 * with different renderers (sprite, geometry). No hardcoded object types.
 *
 * Object types are defined in biome JSON under "roadsideObjects.types[]"
 * Each type specifies its renderer, sprites/geometry, size, clustering, etc.
 */
import * as THREE from 'three';

export class RoadsideSpawner {
    /**
     * @param {Object} options
     * @param {THREE.Camera} options.camera - Camera for billboarding
     * @param {Object} options.worldTranslator - World translator for add/remove
     * @param {Object} options.noise - SimplexNoise instance for natural variation
     * @param {Object} options.poolManager - Optional object pool manager
     * @param {Function} options.getRoadDataAtZ - Callback (z) => { position, tangent, right }
     * @param {Function} options.getTerrainHeightAtPoint - Callback (x, z) => y
     * @param {Function} options.getColorsByCategory - Callback (category) => [colors]
     * @param {Object} config - Configuration from biome JSON
     */
    constructor(options, config = {}) {
        this.camera = options.camera;
        this.worldTranslator = options.worldTranslator;
        this.noise = options.noise;
        this.poolManager = options.poolManager || null;

        // Callbacks to parent for data queries
        this.getRoadDataAtZ = options.getRoadDataAtZ;
        this.getTerrainHeightAtPoint = options.getTerrainHeightAtPoint;
        this.getColorsByCategory = options.getColorsByCategory;

        // Global spawn configuration
        const roadsideConfig = config.roadsideObjects || {};
        this.enabled = roadsideConfig.enabled !== false;
        this.roadWidth = config.roadWidth || 20;
        this.spawnDistance = roadsideConfig.spawnDistance || 700;
        this.density = roadsideConfig.density || 0.3;
        this.minSafeZone = roadsideConfig.minSafeZone || 10;
        this.startDelayChunks = roadsideConfig.startDelayChunks || 2;
        this.distributionWidth = roadsideConfig.distributionWidth || 200;
        this.fadeInDuration = roadsideConfig.fadeInDuration || 0.5;
        this.chunkLength = config.chunkLength || 500;

        // Parse and normalize object types
        this.objectTypes = this._parseObjectTypes(roadsideConfig.types || []);

        // State
        this.objects = [];
        this.lastSpawnZ = 0;
        this.readyToSpawn = false;
        this.spawnDelayFrames = 60;

        // Texture cache: typeId -> texture[]
        this.textureCache = new Map();

        // Color cache per category
        this._colorCache = new Map();
        this._emissiveIntensity = 0.3;

        // Reusable vectors to reduce GC pressure
        this._tempVec1 = new THREE.Vector3();
        this._tempVec2 = new THREE.Vector3();
        this._tempVec3 = new THREE.Vector3();
    }

    /**
     * Parse object types from config and compute cumulative weights
     * @private
     */
    _parseObjectTypes(typesConfig) {
        if (!typesConfig || typesConfig.length === 0) {
            // Default fallback if no types defined
            return [{
                id: 'shrub',
                renderer: 'sprite',
                weight: 1,
                cumulativeWeight: 1,
                minSize: 3,
                maxSize: 10,
                sprites: ['assets/sprites/shrub1.png', 'assets/sprites/shrub2.png', 'assets/sprites/shrub3.png'],
                spriteCount: 3,
                scaleVariation: 0.3,
                stretchVariation: 0.3,
                clusterChance: 0.4,
                clusterSizeMin: 2,
                clusterSizeMax: 4,
                colorCategory: 'vegetation',
                yOffset: 0
            }];
        }

        // Calculate total weight
        let totalWeight = 0;
        for (const type of typesConfig) {
            totalWeight += type.weight || 1;
        }

        // Build normalized types with cumulative weights
        const types = [];
        let cumulativeWeight = 0;

        for (const type of typesConfig) {
            const weight = (type.weight || 1) / totalWeight;
            cumulativeWeight += weight;

            // Parse cluster size (can be number or [min, max] array)
            let clusterSizeMin = 2;
            let clusterSizeMax = 4;
            if (Array.isArray(type.clusterSize)) {
                clusterSizeMin = type.clusterSize[0] || 2;
                clusterSizeMax = type.clusterSize[1] || 4;
            } else if (typeof type.clusterSize === 'number') {
                clusterSizeMin = clusterSizeMax = type.clusterSize;
            }

            types.push({
                id: type.id,
                renderer: type.renderer || 'sprite',
                geometryType: type.geometryType || 'rock',
                weight: weight,
                cumulativeWeight: cumulativeWeight,
                minSize: type.minSize || 3,
                maxSize: type.maxSize || 10,
                sprites: type.sprites || [],
                spriteCount: type.spriteCount || 3,
                scaleVariation: type.scaleVariation ?? 0.3,
                stretchVariation: type.stretchVariation ?? 0.3,
                clusterChance: type.clusterChance ?? 0.4,
                clusterSizeMin: clusterSizeMin,
                clusterSizeMax: clusterSizeMax,
                colorCategory: type.colorCategory || 'vegetation',
                yOffset: type.yOffset || 0,
                embedDepth: type.embedDepth ?? 0.3  // How deep to embed in ground (fraction of size)
            });
        }

        return types;
    }

    /**
     * Update spawner configuration for seamless biome transitions.
     * Called when transitioning to a new biome to update object types.
     * Existing objects remain, new objects use new configuration.
     *
     * @param {Object} roadsideConfig - New roadsideObjects config from biome JSON
     */
    updateConfig(roadsideConfig) {
        if (!roadsideConfig) return;

        // Update global spawn settings
        if (roadsideConfig.enabled !== undefined) this.enabled = roadsideConfig.enabled;
        if (roadsideConfig.spawnDistance !== undefined) this.spawnDistance = roadsideConfig.spawnDistance;
        if (roadsideConfig.density !== undefined) this.density = roadsideConfig.density;
        if (roadsideConfig.minSafeZone !== undefined) this.minSafeZone = roadsideConfig.minSafeZone;
        if (roadsideConfig.distributionWidth !== undefined) this.distributionWidth = roadsideConfig.distributionWidth;
        if (roadsideConfig.fadeInDuration !== undefined) this.fadeInDuration = roadsideConfig.fadeInDuration;

        // Update object types if provided
        if (roadsideConfig.types && roadsideConfig.types.length > 0) {
            this.objectTypes = this._parseObjectTypes(roadsideConfig.types);

            // Load textures for any new sprite types
            this.loadTextures();
        }

        console.log(`[RoadsideSpawner] Updated config with ${this.objectTypes.length} object types`);
    }

    /**
     * Select a random object type based on weights
     * @private
     */
    _selectObjectType() {
        const r = Math.random();
        for (const type of this.objectTypes) {
            if (r <= type.cumulativeWeight) {
                return type;
            }
        }
        return this.objectTypes[this.objectTypes.length - 1];
    }

    /**
     * Load all textures for sprite-based object types
     * @returns {Promise} Resolves when all textures are loaded
     */
    loadTextures() {
        const textureLoader = new THREE.TextureLoader();
        const loadPromises = [];

        for (const type of this.objectTypes) {
            if (type.renderer === 'sprite' && type.sprites && type.sprites.length > 0) {
                const typeTextures = [];
                this.textureCache.set(type.id, typeTextures);

                for (const path of type.sprites) {
                    const promise = new Promise((resolve) => {
                        textureLoader.load(
                            path,
                            (texture) => {
                                typeTextures.push(texture);
                                resolve();
                            },
                            undefined,
                            (error) => {
                                console.warn(`[RoadsideSpawner] Failed to load texture: ${path}`);
                                typeTextures.push(null);
                                resolve();
                            }
                        );
                    });
                    loadPromises.push(promise);
                }
            }
        }

        return Promise.all(loadPromises);
    }

    /**
     * Initialize the spawner (call after terrain is ready)
     */
    initialize() {
        this.readyToSpawn = false;
        this.spawnDelayFrames = 60;
    }

    /**
     * Mark spawner as ready to spawn (call after terrain is fully loaded)
     */
    setReady() {
        this.readyToSpawn = true;
    }

    /**
     * Get color for a category
     * @private
     */
    _getColor(category) {
        const colors = this.getColorsByCategory(category);
        if (!colors || colors.length === 0) return 0x888888;

        const cached = this._colorCache.get(category);
        if (cached !== undefined) return cached;

        return colors[Math.floor(Math.random() * colors.length)];
    }

    /**
     * Create a geometry-based object (rocks, boulders, etc.)
     * @private
     */
    _createGeometryObject(type, x, y, z, color) {
        const size = type.minSize + Math.random() * (type.maxSize - type.minSize);

        // Use icosahedron for organic shapes
        const detail = 2;
        const geometry = new THREE.IcosahedronGeometry(size, detail);

        // Random seed for this object's noise
        const seedX = Math.random() * 1000;
        const seedY = Math.random() * 1000;
        const seedZ = Math.random() * 1000;

        // Deform vertices using coherent noise
        const positions = geometry.attributes.position;
        for (let i = 0; i < positions.count; i++) {
            const vx = positions.getX(i);
            const vy = positions.getY(i);
            const vz = positions.getZ(i);

            const length = Math.sqrt(vx * vx + vy * vy + vz * vz);
            const nx = vx / length;
            const ny = vy / length;
            const nz = vz / length;

            const noiseScale1 = 1.5;
            const noiseScale2 = 3.0;
            const noiseScale3 = 6.0;

            const noise1 = this.noise.noise3D(nx * noiseScale1 + seedX, ny * noiseScale1 + seedY, nz * noiseScale1 + seedZ);
            const noise2 = this.noise.noise3D(nx * noiseScale2 + seedX, ny * noiseScale2 + seedY, nz * noiseScale2 + seedZ);
            const noise3 = this.noise.noise3D(nx * noiseScale3 + seedX, ny * noiseScale3 + seedY, nz * noiseScale3 + seedZ);

            const combinedNoise = noise1 * 0.5 + noise2 * 0.3 + noise3 * 0.2;
            const displacement = 0.9 + combinedNoise * 0.3;
            const newLength = size * displacement;

            positions.setX(i, nx * newLength);
            positions.setY(i, ny * newLength);
            positions.setZ(i, nz * newLength);
        }

        positions.needsUpdate = true;
        geometry.computeVertexNormals();

        const material = new THREE.MeshLambertMaterial({
            color: color,
            flatShading: true
        });

        const mesh = new THREE.Mesh(geometry, material);

        // Embed in ground based on type config
        const embedY = y - size * type.embedDepth;
        mesh.position.set(x, embedY, z);

        mesh.rotation.x = Math.random() * Math.PI * 2;
        mesh.rotation.y = Math.random() * Math.PI * 2;
        mesh.rotation.z = Math.random() * Math.PI * 2;

        const baseScale = 0.7 + Math.random() * 0.6;
        const scaleX = baseScale * (0.8 + Math.random() * 0.4);
        const scaleY = baseScale * (0.6 + Math.random() * 0.5);
        const scaleZ = baseScale * (0.8 + Math.random() * 0.4);
        mesh.scale.set(scaleX, scaleY, scaleZ);

        mesh.castShadow = true;
        mesh.receiveShadow = true;

        mesh.userData.objectTypeId = type.id;
        mesh.userData.renderer = 'geometry';
        mesh.userData.colorCategory = type.colorCategory;
        mesh.userData.spawnTime = performance.now() / 1000;
        mesh.userData.spawned = false;

        return mesh;
    }

    /**
     * Create a sprite-based object (trees, shrubs, grass, etc.)
     * @private
     */
    _createSpriteObject(type, x, y, z, color) {
        const size = type.minSize + Math.random() * (type.maxSize - type.minSize);
        const group = new THREE.Group();

        const textures = this.textureCache.get(type.id) || [];
        const validTextures = textures.filter(t => t !== null);

        for (let i = 0; i < type.spriteCount; i++) {
            let texture = null;
            let material;

            if (validTextures.length > 0) {
                texture = validTextures[Math.floor(Math.random() * validTextures.length)];
            }

            if (texture) {
                material = new THREE.MeshLambertMaterial({
                    map: texture,
                    transparent: true,
                    alphaTest: 0.5,
                    side: THREE.DoubleSide,
                    color: color,
                    emissive: color,
                    emissiveIntensity: this._emissiveIntensity
                });
            } else {
                // Fallback: create gradient blob
                const r = (color >> 16) & 255;
                const g = (color >> 8) & 255;
                const b = color & 255;

                const canvas = document.createElement('canvas');
                canvas.width = 64;
                canvas.height = 64;
                const ctx = canvas.getContext('2d');

                ctx.clearRect(0, 0, 64, 64);
                const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
                gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 1.0)`);
                gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, 0.8)`);
                gradient.addColorStop(0.8, `rgba(${r}, ${g}, ${b}, 0.3)`);
                gradient.addColorStop(1.0, `rgba(${r}, ${g}, ${b}, 0.0)`);
                ctx.fillStyle = gradient;
                ctx.beginPath();
                ctx.arc(32, 32, 32, 0, Math.PI * 2);
                ctx.fill();

                const fallbackTexture = new THREE.CanvasTexture(canvas);
                material = new THREE.MeshLambertMaterial({
                    map: fallbackTexture,
                    transparent: true,
                    alphaTest: 0.1,
                    side: THREE.DoubleSide
                });
            }

            const baseScale = size * (0.6 + Math.random() * 0.8);
            const scaleVariation = 1.0 - type.scaleVariation + Math.random() * (type.scaleVariation * 2);
            const stretchFactor = 1.0 - type.stretchVariation + Math.random() * (type.stretchVariation * 2);
            const width = baseScale * scaleVariation;
            const height = baseScale * scaleVariation * stretchFactor;

            const planeGeom = new THREE.PlaneGeometry(width, height);
            const billboard = new THREE.Mesh(planeGeom, material);

            billboard.userData.ownsFallbackTexture = !texture;

            const offsetX = (Math.random() - 0.5) * size * 0.5;
            const offsetY = height * 0.5 - 1 + type.yOffset;
            const offsetZ = (Math.random() - 0.5) * size * 0.5;
            billboard.position.set(offsetX, offsetY, offsetZ);

            billboard.userData.baseRotationY = Math.random() * Math.PI * 2;

            group.add(billboard);
        }

        group.position.set(x, y, z);
        group.userData.objectTypeId = type.id;
        group.userData.renderer = 'sprite';
        group.userData.colorCategory = type.colorCategory;
        group.userData.spawnTime = performance.now() / 1000;
        group.userData.spawned = false;

        return group;
    }

    /**
     * Create an object of the given type
     * @private
     */
    _createObject(type, x, y, z) {
        const color = this._getColor(type.colorCategory);

        if (type.renderer === 'geometry') {
            return this._createGeometryObject(type, x, y, z, color);
        } else {
            return this._createSpriteObject(type, x, y, z, color);
        }
    }

    /**
     * Acquire an object from pool or create directly
     */
    acquireObject(type, x, y, z) {
        let obj;
        const color = this._getColor(type.colorCategory);

        if (this.poolManager) {
            obj = this.poolManager.acquire(type.id, { color });
            if (!obj) return null;

            obj.position.set(x, y, z);
            obj.visible = true;
        } else {
            obj = this._createObject(type, x, y, z);
        }

        // Apply current emissive intensity to sprites
        if (type.renderer === 'sprite' && this._emissiveIntensity !== undefined) {
            obj.children.forEach(mesh => {
                if (mesh.material && mesh.material.emissive) {
                    mesh.material.emissiveIntensity = this._emissiveIntensity;
                }
            });
        }

        return obj;
    }

    /**
     * Release an object back to pool or dispose
     */
    releaseObject(obj) {
        if (this.poolManager) {
            obj.visible = false;
            this.poolManager.release(obj);
        } else {
            if (obj.userData.renderer === 'geometry') {
                obj.geometry.dispose();
                obj.material.dispose();
            } else if (obj.userData.renderer === 'sprite') {
                obj.children.forEach(mesh => {
                    mesh.geometry.dispose();
                    if (mesh.userData.ownsFallbackTexture && mesh.material.map) {
                        mesh.material.map.dispose();
                    }
                    mesh.material.dispose();
                });
            }
        }
    }

    /**
     * Get natural-looking distance from road using weighted distribution
     */
    getNaturalDistanceFromRoad() {
        const u1 = Math.random();
        const u2 = Math.random();
        const gaussian = Math.max(0, Math.min(1, 0.4 + (Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2)) * 0.25));

        return (this.roadWidth / 2) + this.minSafeZone + gaussian * this.distributionWidth;
    }

    /**
     * Spawn a cluster of objects around a central point
     */
    spawnCluster(centerZ, side, type, clusterSize) {
        const roadData = this.getRoadDataAtZ(centerZ);
        if (!roadData) return;

        const sideMultiplier = side === 'left' ? -1 : 1;
        const baseDistance = this.getNaturalDistanceFromRoad();

        for (let i = 0; i < clusterSize; i++) {
            const zOffset = (Math.random() - 0.5) * 15;
            const distanceOffset = (Math.random() - 0.5) * 30;

            const clusterZ = centerZ + zOffset;
            const clusterRoadData = this.getRoadDataAtZ(clusterZ);
            if (!clusterRoadData) continue;

            const distanceFromRoad = Math.max(
                (this.roadWidth / 2) + this.minSafeZone,
                baseDistance + distanceOffset
            );

            const offset = this._tempVec3.copy(clusterRoadData.right).multiplyScalar(sideMultiplier * distanceFromRoad);
            const x = clusterRoadData.position.x + offset.x;
            const z = clusterRoadData.position.z + offset.z;

            const terrainY = this.getTerrainHeightAtPoint(x, z);
            if (terrainY === null) continue;

            const obj = this.acquireObject(type, x, terrainY, z);
            if (obj) {
                this.worldTranslator.add(obj);
                this.objects.push(obj);
            }
        }
    }

    /**
     * Spawn a single object at the given position
     */
    spawnSingleObject(z, side, type) {
        const roadData = this.getRoadDataAtZ(z);
        if (!roadData) return;

        const sideMultiplier = side === 'left' ? -1 : 1;
        const distanceFromRoad = this.getNaturalDistanceFromRoad();

        const offset = this._tempVec3.copy(roadData.right).multiplyScalar(sideMultiplier * distanceFromRoad);
        const x = roadData.position.x + offset.x;
        const objZ = roadData.position.z + offset.z;

        const terrainY = this.getTerrainHeightAtPoint(x, objZ);
        if (terrainY === null) return;

        const obj = this.acquireObject(type, x, terrainY, objZ);
        if (obj) {
            this.worldTranslator.add(obj);
            this.objects.push(obj);
        }
    }

    /**
     * Main spawn loop - spawns objects ahead of camera
     */
    spawn() {
        if (!this.enabled) return;
        if (!this.readyToSpawn) return;
        if (this.objectTypes.length === 0) return;

        // Wait for frame delay after initialization
        if (this.spawnDelayFrames > 0) {
            this.spawnDelayFrames--;
            return;
        }

        const groupOffset = this.worldTranslator ? this.worldTranslator.getGroupOffset() : 0;
        const targetZ = -groupOffset - this.spawnDistance;

        // Skip spawning in first N chunks
        const minSpawnZ = -this.startDelayChunks * this.chunkLength;
        if (this.lastSpawnZ > minSpawnZ) {
            this.lastSpawnZ = minSpawnZ;
        }

        const maxSpawnsPerFrame = 15;
        let spawnsThisFrame = 0;
        const baseStep = 8;

        while (this.lastSpawnZ > targetZ && spawnsThisFrame < maxSpawnsPerFrame) {
            const noiseZ = this.lastSpawnZ;

            // Use noise to vary density
            const densityNoise = this.noise.noise(noiseZ * 0.01, 500);
            const localDensity = this.density * (0.3 + (densityNoise + 1) * 0.7);

            if (Math.random() < localDensity) {
                spawnsThisFrame++;

                // Select object type based on weights
                const type = this._selectObjectType();

                // Determine cluster vs single based on type's cluster settings
                const isCluster = Math.random() < type.clusterChance;
                const clusterSize = isCluster
                    ? type.clusterSizeMin + Math.floor(Math.random() * (type.clusterSizeMax - type.clusterSizeMin + 1))
                    : 1;

                // Choose side based on noise
                const sideNoise = this.noise.noise(noiseZ * 0.02, 100);
                const side = sideNoise > 0.1 ? 'right' : sideNoise < -0.1 ? 'left' : (Math.random() < 0.5 ? 'left' : 'right');

                const bothSides = Math.random() < 0.15;

                if (isCluster) {
                    this.spawnCluster(this.lastSpawnZ, side, type, clusterSize);
                    if (bothSides) {
                        this.spawnCluster(this.lastSpawnZ, side === 'left' ? 'right' : 'left', type, Math.max(1, clusterSize - 1));
                    }
                } else {
                    this.spawnSingleObject(this.lastSpawnZ, side, type);
                    if (bothSides) {
                        this.spawnSingleObject(this.lastSpawnZ, side === 'left' ? 'right' : 'left', type);
                    }
                }
            }

            // Jittered step
            const jitter = (Math.random() - 0.5) * baseStep * 0.5;
            this.lastSpawnZ -= baseStep + jitter;
        }
    }

    /**
     * Update all roadside objects (fade-in, billboarding, cleanup)
     */
    update() {
        if (!this.enabled) return;

        const currentTime = performance.now() / 1000;
        const cameraWorldPos = this.camera.getWorldPosition(this._tempVec1);

        for (let i = this.objects.length - 1; i >= 0; i--) {
            const obj = this.objects[i];

            // Handle fade-in effect
            if (!obj.userData.spawned) {
                const age = currentTime - obj.userData.spawnTime;
                if (age < this.fadeInDuration) {
                    const fadeProgress = age / this.fadeInDuration;
                    if (obj.userData.renderer === 'geometry') {
                        obj.material.opacity = fadeProgress;
                        obj.material.transparent = fadeProgress < 1.0;
                    } else if (obj.userData.renderer === 'sprite') {
                        if (!obj.userData.targetScale) {
                            obj.userData.targetScale = obj.scale.clone();
                        }
                        const scaleProgress = fadeProgress * fadeProgress;
                        obj.scale.copy(obj.userData.targetScale).multiplyScalar(scaleProgress);
                    }
                } else {
                    obj.userData.spawned = true;
                    if (obj.userData.renderer === 'geometry') {
                        obj.material.transparent = false;
                        obj.material.opacity = 1.0;
                    } else if (obj.userData.renderer === 'sprite') {
                        if (obj.userData.targetScale) {
                            obj.scale.copy(obj.userData.targetScale);
                            delete obj.userData.targetScale;
                        }
                    }
                }
            }

            // Billboard sprites to face camera (Y-axis only)
            if (obj.userData.renderer === 'sprite') {
                obj.children.forEach(mesh => {
                    const meshWorldPos = mesh.getWorldPosition(this._tempVec2);
                    const dx = cameraWorldPos.x - meshWorldPos.x;
                    const dz = cameraWorldPos.z - meshWorldPos.z;
                    mesh.rotation.y = Math.atan2(dx, dz);
                });
            }

            // Remove objects far behind camera
            if (this.worldTranslator.isPastCamera(obj.position.z, 200)) {
                this.worldTranslator.remove(obj);
                this.releaseObject(obj);
                obj.userData.markedForRemoval = true;
            }
        }

        // O(1) array compaction
        let writeIndex = 0;
        for (let i = 0; i < this.objects.length; i++) {
            if (!this.objects[i].userData.markedForRemoval) {
                this.objects[writeIndex++] = this.objects[i];
            }
        }
        this.objects.length = writeIndex;

        // Spawn new objects
        this.spawn();
    }

    /**
     * Update object colors by category (called by ColorCycleManager)
     * @param {Map<string, number>} colorsByCategory - Map of category -> color
     * @param {number} vegetationEmissiveIntensity - Emissive intensity for vegetation
     */
    updateColors(colorsByCategory, vegetationEmissiveIntensity = 0.3) {
        // Check if anything changed
        let changed = this._emissiveIntensity !== vegetationEmissiveIntensity;
        if (!changed) {
            for (const [category, color] of colorsByCategory) {
                if (this._colorCache.get(category) !== color) {
                    changed = true;
                    break;
                }
            }
        }

        if (!changed) return;

        // Update cache
        this._emissiveIntensity = vegetationEmissiveIntensity;
        for (const [category, color] of colorsByCategory) {
            this._colorCache.set(category, color);
        }

        // Update existing objects
        for (let i = 0; i < this.objects.length; i++) {
            const obj = this.objects[i];
            if (!obj || !obj.userData) continue;

            const color = colorsByCategory.get(obj.userData.colorCategory);
            if (color === undefined) continue;

            if (obj.userData.renderer === 'geometry') {
                if (obj.material) {
                    obj.material.color.setHex(color);
                }
            } else if (obj.userData.renderer === 'sprite') {
                obj.children.forEach(mesh => {
                    if (mesh.material) {
                        mesh.material.color.setHex(color);
                        if (mesh.material.emissive) {
                            mesh.material.emissive.setHex(color);
                            mesh.material.emissiveIntensity = vegetationEmissiveIntensity;
                        }
                    }
                });
            }
        }

        // Update via pool manager if available
        if (this.poolManager) {
            const rockColor = colorsByCategory.get('rock');
            if (rockColor !== undefined) {
                this.poolManager.setRockColor(rockColor);
            }
        }
    }

    /**
     * Get number of active objects (for debugging)
     */
    getObjectCount() {
        return this.objects.length;
    }

    /**
     * Dispose all resources
     */
    dispose() {
        // Clean up all objects
        for (let i = 0; i < this.objects.length; i++) {
            const obj = this.objects[i];
            this.worldTranslator.remove(obj);
            this.releaseObject(obj);
        }
        this.objects = [];

        // Dispose all cached textures
        for (const textures of this.textureCache.values()) {
            for (const texture of textures) {
                if (texture) texture.dispose();
            }
        }
        this.textureCache.clear();
    }
}
